import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'

// Miroir des réponses boostcore `quote/admin/*` (QuoteController, super-admin).

/** Résumé de synchro renvoyé par POST quote/admin/company/:uuid/sync. */
export interface QuotesSyncSummary {
  totalCountParsed: number
  unchanged: number
  created: number
  updated: number
  errors: Array<{ quoteRef?: string; source?: string; message: string }>
  linesSynced: number
}

/** Devis confirmé renvoyé par GET quote/admin/company/:uuid/confirmed. */
export interface ConfirmedQuoteRow {
  uuid: string
  quoteReference: string
  customerReference: string | null
  dateOrder: string | null
  signedAt: string | null
  totalCa: number
  totalCaUntaxed: number | null
  lineCount: number
  linesSubtotal: number
}

/** Ligne produit renvoyée par POST quote/admin/lines. */
export interface QuoteLineRow {
  uuid: string
  quoteUuid: string
  quoteReference: string
  productOdooId: number | null
  productName: string
  productCategory: string | null
  description: string | null
  quantity: number
  priceUnit: number
  priceSubtotal: number
  priceTotal: number | null
  /** Coût unitaire (sale.order.line.purchase_price Odoo) — pour la marge. */
  purchasePrice: number | null
}

export const useQuotesExportAdminStore = defineStore('quotesExportAdmin', () => {
  const suiteStore = useSuiteStore()

  const quotes = ref<ConfirmedQuoteRow[]>([])
  const lines = ref<QuoteLineRow[]>([])
  const isSyncing = ref(false)
  const isLoadingQuotes = ref(false)
  const isLoadingLines = ref(false)

  /** Synchronise les devis + lignes d'une company depuis Odoo. */
  async function syncCompany(
    companyUuid: string,
    opts: { source?: string; limit?: number } = {},
  ): Promise<{ ok: boolean; data?: QuotesSyncSummary; message?: string }> {
    isSyncing.value = true
    try {
      const qs = new URLSearchParams()
      if (opts.source) qs.set('source', opts.source)
      if (opts.limit) qs.set('limit', String(opts.limit))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      const res = await suiteStore.suiteFetch(`quote/admin/company/${encodeURIComponent(companyUuid)}/sync${suffix}`, {
        method: 'POST',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        return { ok: true, data: (await res.json()) as QuotesSyncSummary }
      }
      return { ok: false, message: await extractError(res, 'Échec de la synchronisation') }
    } catch (e) {
      return { ok: false, message: (e as Error).message }
    } finally {
      isSyncing.value = false
    }
  }

  /** Charge les devis confirmés (sale/done) d'une company, filtrés par date_order. */
  async function fetchConfirmed(companyUuid: string, from?: string, to?: string): Promise<ConfirmedQuoteRow[]> {
    isLoadingQuotes.value = true
    try {
      const qs = new URLSearchParams()
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      const res = await suiteStore.suiteFetch(`quote/admin/company/${encodeURIComponent(companyUuid)}/confirmed${suffix}`, {
        method: 'GET',
        credentials: 'include',
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const json = (await res.json()) as { data?: ConfirmedQuoteRow[] }
        quotes.value = json?.data ?? []
        return quotes.value
      }
    } catch (e) {
      console.error('Erreur récupération devis confirmés', e)
    } finally {
      isLoadingQuotes.value = false
    }
    quotes.value = []
    return []
  }

  /** Charge les lignes produit des devis sélectionnés (preview / export). */
  async function fetchLines(quoteUuids: string[]): Promise<QuoteLineRow[]> {
    if (!quoteUuids.length) {
      lines.value = []
      return []
    }
    isLoadingLines.value = true
    try {
      const res = await suiteStore.suiteFetch('quote/admin/lines', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ quoteUuids }),
      })
      if (res && typeof res !== 'boolean' && res.ok) {
        const json = (await res.json()) as { data?: QuoteLineRow[] }
        lines.value = json?.data ?? []
        return lines.value
      }
    } catch (e) {
      console.error('Erreur récupération lignes de devis', e)
    } finally {
      isLoadingLines.value = false
    }
    lines.value = []
    return []
  }

  return {
    quotes,
    lines,
    isSyncing,
    isLoadingQuotes,
    isLoadingLines,
    syncCompany,
    fetchConfirmed,
    fetchLines,
  }
})

/** Extrait un message d'erreur lisible d'une réponse fetch non-ok. */
async function extractError(res: Response | boolean | undefined, fallback: string): Promise<string> {
  if (!res || typeof res === 'boolean') return fallback
  try {
    const b = await res.json()
    if (b?.message) return Array.isArray(b.message) ? b.message.join(', ') : String(b.message)
  } catch {
    // ignore
  }
  return fallback
}
